import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminLangBar from "./AdminLangBar";
import { api, resolveImage } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, X } from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";
import ImageUploader from "../../components/ImageUploader";

const EMPTY = {
  slug: "",
  name: "",
  developer: "",
  tagline: "",
  propertyType: "",
  categories: [],
  priceFrom: "",
  priceFromValue: 0,
  bedrooms: "",
  builtSizes: "",
  terraceSizes: "",
  completion: "",
  units: "",
  views: "",
  location: "",
  image: "",
  gallery: [],
  intro: "",
  why: "",
  amenities: [],
  buyerProfile: [],
  seo: { title: "", description: "" },
  order: 100,
};

const CATEGORY_OPTIONS = [
  "apartments",
  "penthouses",
  "villas",
  "frontline-golf",
  "sea-view",
];

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

function FieldLabel({ children }) {
  return (
    <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
      {children}
    </label>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-[#E5DFD5] py-2.5 text-sm focus:outline-none focus:border-[#C5A880]";

function ListEditor({ value, onChange, placeholder, testId }) {
  const add = () => onChange([...(value || []), ""]);
  const update = (i, v) => {
    const next = [...value];
    next[i] = v;
    onChange(next);
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testId} className="space-y-2">
      {(value || []).map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={v}
            onChange={(e) => update(i, e.target.value)}
            placeholder={placeholder}
            className={inputCls}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-[#73706B] hover:text-red-700 p-1"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C5A880] hover:text-[#1A1A1A] mt-2"
      >
        <Plus className="w-3 h-3" strokeWidth={1.5} /> Add
      </button>
    </div>
  );
}

export default function AdminDevelopmentEdit({ mode }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = mode === "new";
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/developments/${slug}`, { params: { lang } });
        setForm({ ...EMPTY, ...data, seo: { ...EMPTY.seo, ...(data.seo || {}) } });
      } catch {
        toast.error("Failed to load");
        navigate("/admin/developments");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isNew, navigate, lang, reloadTick]);

  const update = (k) => (e) =>
    setForm({ ...form, [k]: typeof e === "string" ? e : e.target ? e.target.value : e });

  const toggleCategory = (c) => {
    const has = form.categories.includes(c);
    setForm({
      ...form,
      categories: has ? form.categories.filter((x) => x !== c) : [...form.categories, c],
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Name is required");
      return;
    }
    const payload = {
      ...form,
      slug: form.slug || slugify(form.name),
      priceFromValue: Number(form.priceFromValue) || 0,
      order: Number(form.order) || 0,
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/developments", payload);
        toast.success("Development created");
        navigate("/admin/developments");
      } else {
        await api.put(`/admin/developments/${slug}`, payload, { params: { lang } });
        toast.success(lang === "en" ? "Saved" : `Saved (${lang.toUpperCase()} translation)`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(" ")
          : "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="p-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      </div>
    );

  return (
    <div className="p-10 lg:p-16 max-w-[1100px]" data-testid="admin-dev-edit">
      <Link
        to="/admin/developments"
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A] mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All Developments
      </Link>
      <h1
        className="text-4xl lg:text-5xl font-light mb-2"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {isNew ? "New Development" : form.name}
      </h1>
      <p className="text-sm text-[#73706B] mb-12">
        All fields below appear on the live page.
      </p>

      <AdminLangBar lang={lang} onChange={setLang} kind="development" docKey={slug} disabled={isNew} onTranslated={() => setReloadTick((n) => n + 1)} />

      <form onSubmit={save} className="space-y-12">
        {/* Identity */}
        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">
            — Identity
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FieldLabel>Name *</FieldLabel>
              <input
                required
                value={form.name}
                onChange={update("name")}
                data-testid="dev-field-name"
                className={inputCls}
              />
            </div>
            <div>
              <FieldLabel>Slug (URL)</FieldLabel>
              <input
                value={form.slug}
                onChange={update("slug")}
                placeholder={slugify(form.name)}
                data-testid="dev-field-slug"
                className={inputCls}
              />
            </div>
            <div>
              <FieldLabel>Developer</FieldLabel>
              <input value={form.developer} onChange={update("developer")} data-testid="dev-field-developer" className={inputCls} />
            </div>
            <div>
              <FieldLabel>Property Type</FieldLabel>
              <input value={form.propertyType} onChange={update("propertyType")} data-testid="dev-field-propertyType" className={inputCls} />
            </div>
            <div className="md:col-span-2">
              <FieldLabel>Tagline</FieldLabel>
              <input value={form.tagline} onChange={update("tagline")} data-testid="dev-field-tagline" className={inputCls} />
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="space-y-3">
          <FieldLabel>Categories</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_OPTIONS.map((c) => {
              const on = form.categories.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCategory(c)}
                  data-testid={`dev-cat-${c}`}
                  className={`px-4 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors ${
                    on
                      ? "bg-[#1A1A1A] text-[#F9F6F0]"
                      : "border border-[#E5DFD5] text-[#1A1A1A] hover:border-[#1A1A1A]"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </section>

        {/* Pricing & Size */}
        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">
            — Pricing & Size
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <FieldLabel>Price From (display)</FieldLabel>
              <input value={form.priceFrom} onChange={update("priceFrom")} data-testid="dev-field-priceFrom" className={inputCls} />
            </div>
            <div>
              <FieldLabel>Price From (number for filters)</FieldLabel>
              <input
                type="number"
                value={form.priceFromValue}
                onChange={update("priceFromValue")}
                data-testid="dev-field-priceFromValue"
                className={inputCls}
              />
            </div>
            <div>
              <FieldLabel>Bedrooms</FieldLabel>
              <input value={form.bedrooms} onChange={update("bedrooms")} data-testid="dev-field-bedrooms" className={inputCls} />
            </div>
            <div>
              <FieldLabel>Built Sizes</FieldLabel>
              <input value={form.builtSizes} onChange={update("builtSizes")} data-testid="dev-field-builtSizes" className={inputCls} />
            </div>
            <div>
              <FieldLabel>Terrace Sizes</FieldLabel>
              <input value={form.terraceSizes} onChange={update("terraceSizes")} data-testid="dev-field-terraceSizes" className={inputCls} />
            </div>
            <div>
              <FieldLabel>Completion</FieldLabel>
              <input value={form.completion} onChange={update("completion")} data-testid="dev-field-completion" className={inputCls} />
            </div>
            <div>
              <FieldLabel>Units</FieldLabel>
              <input value={form.units} onChange={update("units")} data-testid="dev-field-units" className={inputCls} />
            </div>
            <div>
              <FieldLabel>Views</FieldLabel>
              <input value={form.views} onChange={update("views")} data-testid="dev-field-views" className={inputCls} />
            </div>
            <div>
              <FieldLabel>Location</FieldLabel>
              <input value={form.location} onChange={update("location")} data-testid="dev-field-location" className={inputCls} />
            </div>
          </div>
        </section>

        {/* Hero image + gallery */}
        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">
            — Images
          </h2>
          <ImageUploader
            label="Hero Image"
            value={form.image}
            onChange={(v) => setForm({ ...form, image: v })}
            testId="dev-image"
          />
          <div>
            <FieldLabel>Gallery (image URLs / uploads)</FieldLabel>
            <div className="space-y-3">
              {form.gallery.map((g, i) => (
                <ImageUploader
                  key={i}
                  label={`Gallery image ${i + 1}`}
                  value={g}
                  onChange={(v) => {
                    const next = [...form.gallery];
                    next[i] = v;
                    setForm({ ...form, gallery: next });
                  }}
                  testId={`dev-gallery-${i}`}
                />
              ))}
              <button
                type="button"
                onClick={() => setForm({ ...form, gallery: [...form.gallery, ""] })}
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C5A880] hover:text-[#1A1A1A]"
              >
                <Plus className="w-3 h-3" strokeWidth={1.5} /> Add gallery image
              </button>
            </div>
          </div>
        </section>

        {/* Copy */}
        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Copy</h2>
          <div>
            <FieldLabel>Sales Introduction (rich text)</FieldLabel>
            <div data-testid="dev-field-intro">
              <RichTextEditor value={form.intro} onChange={(v) => setForm({ ...form, intro: v })} />
            </div>
          </div>
          <div>
            <FieldLabel>Why this development is different</FieldLabel>
            <textarea
              value={form.why}
              onChange={update("why")}
              rows={3}
              data-testid="dev-field-why"
              className={`${inputCls} resize-none`}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <FieldLabel>Amenities</FieldLabel>
              <ListEditor
                value={form.amenities}
                onChange={(v) => setForm({ ...form, amenities: v })}
                placeholder="e.g. Heated infinity pool"
                testId="dev-amenities"
              />
            </div>
            <div>
              <FieldLabel>Buyer Suitability</FieldLabel>
              <ListEditor
                value={form.buyerProfile}
                onChange={(v) => setForm({ ...form, buyerProfile: v })}
                placeholder="e.g. Holiday home buyers"
                testId="dev-buyers"
              />
            </div>
          </div>
        </section>

        {/* SEO */}
        <section className="space-y-6" data-testid="dev-seo-section">
          <div>
            <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— SEO (per-development)</h2>
            <p className="text-xs text-[#73706B] mt-2">
              Custom search-engine title and Google snippet for this specific development. Leave blank to fall back to the site-wide default.
            </p>
          </div>
          <div>
            <FieldLabel>SEO Title (browser tab &amp; Google result)</FieldLabel>
            <input
              value={form.seo?.title || ""}
              onChange={(e) => setForm({ ...form, seo: { ...form.seo, title: e.target.value } })}
              placeholder={form.name ? `${form.name} at Finca Cortesin | Luxury New Development, Casares` : "Custom SEO title for this development"}
              data-testid="dev-field-seo-title"
              className={inputCls}
              maxLength={70}
            />
            <p className="text-[10px] text-[#9A968D] mt-1">
              {(form.seo?.title || "").length}/70 — Google typically shows the first 60 characters.
            </p>
          </div>
          <div>
            <FieldLabel>Meta Description (Google snippet)</FieldLabel>
            <textarea
              value={form.seo?.description || ""}
              onChange={(e) => setForm({ ...form, seo: { ...form.seo, description: e.target.value } })}
              rows={3}
              placeholder={form.tagline || `${form.name || "This development"} — new luxury residences inside Finca Cortesín. Include bedroom range, price from, completion date and location cues.`}
              data-testid="dev-field-seo-description"
              className={`${inputCls} resize-none`}
              maxLength={170}
            />
            <p className="text-[10px] text-[#9A968D] mt-1">
              {(form.seo?.description || "").length}/170 — Google typically shows the first 155-160 characters.
            </p>
          </div>
          <div>
            <FieldLabel>Display Order (lower = first)</FieldLabel>
            <input
              type="number"
              value={form.order}
              onChange={update("order")}
              className={`${inputCls} max-w-[120px]`}
            />
          </div>
        </section>

        <div className="flex items-center gap-4 border-t border-[#E5DFD5] pt-8">
          <button
            type="submit"
            disabled={saving}
            data-testid="dev-save"
            className="bg-[#1A1A1A] text-[#F9F6F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
            {saving ? "Saving" : isNew ? "Create Development" : "Save Changes"}
          </button>
          <Link
            to="/admin/developments"
            className="text-[11px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A]"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
